import React from 'react'
import { MenuItem, SelectChangeEvent } from '@mui/material'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import { makeStyles } from '@mui/styles'
import { ReactComponent as ArrowDownIcon } from '../../../assets/images/arrows/down-bold.svg'
import { useBooleanState } from '../../hooks/useBooleanState'

export type TagSelectOption = {
    id: string
    label: string
}

export type TagSelectProps = {
    value: string
    options: TagSelectOption[]
    onChange: (optionId: string) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    isLoading?: boolean
    // Optional per-value pill background/text color override -- e.g. a status picker (green
    // for active, gray for archived) rather than the flat primary3 pill every tier picker uses.
    // Callers resolve any theme tokens themselves (via useTheme()) before returning colors here,
    // so this stays a plain string map rather than threading the theme through this component.
    // Falls back to the default flat pill for any value it returns undefined for.
    pillColor?: (optionId: string) => { bg: string; text: string } | undefined
}

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: '16px !important',
        backgroundColor: `${theme.palette.figma.grayscale.tier7} !important`,
        boxShadow: '0px 8px 16px 0px rgba(0, 0, 0, 0.17); !important',
        transition: 'none !important',
        maxHeight: '300px !important',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: `${theme.spacing(1.5)} !important`,
        padding: `${theme.spacing(2)} !important`,
        '& > li': {
            minHeight: 'auto !important',
        },
    },
}))

const SelectArrowIcon = ({ className }: { className: string }) => (
    <SvgIcon
        className={className}
        component={ArrowDownIcon}
        inheritViewBox
        color="inherit"
        sx={{ fontSize: '16px', color: 'inherit !important' }}
    />
)

export const TagSelect = ({
    value,
    options,
    onChange,
    label,
    placeholder = 'Not set',
    disabled = false,
    isLoading = false,
    pillColor,
}: TagSelectProps) => {
    const styles = useStyles()
    const open = useBooleanState()

    const isDisabled = disabled || isLoading || !options.length
    const pill = pillColor?.(value)

    const handleChange = (event: SelectChangeEvent<unknown>) => {
        const nextOptionId = event.target.value as string

        if (nextOptionId !== value) {
            onChange(nextOptionId)
        }
    }

    const renderValue = () => (
        <Typography variant="14x400" color="inherit" whiteSpace="nowrap">
            {options.find((option) => option.id === value)?.label ?? placeholder}
        </Typography>
    )

    return (
        <Select
            disabled={isDisabled}
            inputProps={{ 'aria-label': label }}
            MenuProps={{
                classes: { paper: styles.paper, list: styles.list },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
            }}
            open={open.state}
            onClose={open.handleSetAsFalse}
            onOpen={open.handleSetAsTrue}
            variant="outlined"
            value={value ?? ''}
            onChange={handleChange}
            displayEmpty
            renderValue={renderValue}
            IconComponent={SelectArrowIcon}
            sx={(theme) => ({
                borderRadius: '16px',
                color: pill
                    ? pill.text
                    : open.state
                      ? theme.palette.figma.primary
                      : theme.palette.figma.grayscale.textHier1,
                '& .MuiOutlinedInput-notchedOutline': {
                    border: `unset !important`,
                },
                '& .MuiOutlinedInput-input': {
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '16px',
                    backgroundColor: pill ? pill.bg : theme.palette.figma.primary3,
                    py: `${theme.spacing(0.25)} !important`,
                    pl: `${theme.spacing(1)} !important`,
                    pr: `${theme.spacing(3)} !important`,
                    '&.Mui-disabled': {
                        cursor: 'not-allowed',
                        color: theme.palette.figma.grayscale.tier4,
                        '-webkit-text-fill-color': `${theme.palette.figma.grayscale.tier4} !important`,
                    },
                },
                '& .MuiSelect-icon': {
                    right: theme.spacing(0.75),
                    color: `${
                        pill
                            ? pill.text
                            : open.state
                              ? theme.palette.figma.primary
                              : theme.palette.figma.grayscale.textHier1
                    } !important`,
                },
                '&.Mui-disabled .MuiSelect-icon': {
                    color: `${theme.palette.figma.grayscale.tier4} !important`,
                },
            })}
        >
            {options.map((option) => {
                const isSelected = value === option.id

                return (
                    <MenuItem
                        key={option.id}
                        selected={isSelected}
                        value={option.id}
                        disableRipple
                        sx={(theme) => ({
                            flex: 1,
                            p: 0,
                            borderRadius: '8px',
                            color: isSelected ? 'figma.primary' : 'figma.grayscale.textHier2',
                            backgroundColor: isSelected
                                ? `${theme.palette.figma.primary3} !important`
                                : 'unset !important',
                            mx: isSelected ? -1 : 0,
                            my: isSelected ? -0.5 : 0,
                            px: isSelected ? 1 : 0,
                            py: isSelected ? 0.5 : 0,
                            '&:hover': {
                                color: 'figma.primary',
                            },
                        })}
                    >
                        <Stack direction="row" gap={1}>
                            <Typography variant="16x500" color="inherit" sx={{ mt: -0.25 }}>
                                {option.label}
                            </Typography>
                        </Stack>
                    </MenuItem>
                )
            })}
        </Select>
    )
}

export default TagSelect
